import React, { useMemo } from 'react';
import { usePnl, useToday, useWeek, useTrades } from '@/hooks/useDashboardQueries';
import { PageHeader } from '@/components/common/PageHeader';
import { MetricCard } from '@/components/common/MetricCard';
import { StatusBadge } from '@/components/common/StatusBadge';
import { PnlValue } from '@/components/common/PnlValue';
import { ErrorState } from '@/components/common/ErrorState';
import { MetricCardSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { formatINR } from '@/lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
  AreaChart,
  Area,
  CartesianGrid,
} from 'recharts';
import {
  BarChart3,
  TrendingUp,
  Award,
  PieChart,
  Scale,
} from 'lucide-react';

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; name?: string; dataKey?: string }>;
  label?: string;
}

const CustomPnlTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const value = payload[0].value;
    return (
      <div className="p-2.5 rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface-elevated)] shadow-xl text-xs font-mono">
        <div className="text-[var(--text-muted)] mb-1">{label}</div>
        <div className="flex items-center gap-1.5 font-bold">
          <span className="text-[var(--text-secondary)]">P&L:</span>
          <span className={value >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
            {formatINR(value, { showSign: true })}
          </span>
        </div>
      </div>
    );
  }
  return null;
};

export const AnalyticsView: React.FC = () => {
  const {
    data: pnlData,
    isLoading: isPnlLoading,
    isError: isPnlError,
    error: pnlError,
    refetch: refetchPnl,
  } = usePnl();

  const { data: todayData } = useToday();
  const { data: weekData } = useWeek();
  const { data: tradesData } = useTrades();

  const pnl = pnlData?.summary;
  const rawHistory = useMemo(() => weekData?.days || pnlData?.daily_history || [], [weekData, pnlData]);
  const trades = useMemo(() => tradesData?.trades || [], [tradesData]);

  // Compute cumulative P&L curve data & Max Drawdown
  const { cumulativeData, maxDrawdown } = useMemo(() => {
    let runningTotal = 0;
    let peak = 0;
    let maxDd = 0;

    const points = rawHistory.map((day) => {
      runningTotal += day.pnl;
      if (runningTotal > peak) {
        peak = runningTotal;
      }
      const currentDd = peak - runningTotal;
      if (currentDd > maxDd) {
        maxDd = currentDd;
      }
      return {
        date: day.date,
        dailyPnl: day.pnl,
        cumulativePnl: runningTotal,
        tradesCount: day.trades_count,
      };
    });

    return {
      cumulativeData: points,
      maxDrawdown: points.length > 0 ? maxDd : null,
    };
  }, [rawHistory]);

  // Derived Closed Trade Breakdown
  const {
    closedWithPnl,
    winningTradesList,
    losingTradesList,
    bestTradeVal,
    worstTradeVal,
    avgWinVal,
    avgLossVal,
    calculatedWinRate,
    calculatedProfitFactor,
  } = useMemo(() => {
    const closed = trades.filter((t) => t.pnl !== null && t.pnl !== undefined);
    const wins = closed.filter((t) => (t.pnl || 0) > 0);
    const losses = closed.filter((t) => (t.pnl || 0) < 0);

    const best = wins.length > 0 ? Math.max(...wins.map((t) => t.pnl || 0)) : null;
    const worst = losses.length > 0 ? Math.min(...losses.map((t) => t.pnl || 0)) : null;

    const grossProfit = wins.reduce((acc, t) => acc + (t.pnl || 0), 0);
    const grossLoss = losses.reduce((acc, t) => acc + Math.abs(t.pnl || 0), 0);

    const avgWin = wins.length > 0 ? grossProfit / wins.length : (pnl?.average_win ?? null);
    const avgLoss = losses.length > 0 ? -grossLoss / losses.length : (pnl?.average_loss ?? null);

    let winRate: number | null = null;
    if (closed.length > 0) {
      winRate = (wins.length / closed.length) * 100;
    } else if (pnl?.win_rate !== undefined && pnl.win_rate !== null && (pnl.total_trades || 0) > 0) {
      winRate = pnl.win_rate;
    }

    let pf: string = '—';
    if (pnl?.profit_factor !== undefined && pnl.profit_factor !== null) {
      pf = pnl.profit_factor.toFixed(2);
    } else if (closed.length > 0) {
      if (grossLoss === 0) {
        pf = grossProfit > 0 ? '∞' : '—';
      } else {
        pf = (grossProfit / grossLoss).toFixed(2);
      }
    }

    return {
      closedWithPnl: closed,
      winningTradesList: wins,
      losingTradesList: losses,
      bestTradeVal: best,
      worstTradeVal: worst,
      avgWinVal: avgWin,
      avgLossVal: avgLoss,
      calculatedWinRate: winRate,
      calculatedProfitFactor: pf,
    };
  }, [trades, pnl]);

  const totalRealizedPnl = pnl?.total_pnl ?? pnl?.realized_pnl ?? 0;
  const winsCount = winningTradesList.length || pnl?.winning_trades || 0;
  const lossesCount = losingTradesList.length || pnl?.losing_trades || 0;
  const totalCompletedCount = closedWithPnl.length || (winsCount + lossesCount);

  if (isPnlLoading && !pnl) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="P&L Analytics"
          icon={BarChart3}
        />
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <MetricCardSkeleton key={i} />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-72 rounded-lg bg-slate-900/40 border border-slate-800 animate-pulse" />
          <div className="h-72 rounded-lg bg-slate-900/40 border border-slate-800 animate-pulse" />
        </div>
      </div>
    );
  }

  if (isPnlError && !pnl) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="P&L Analytics"
          icon={BarChart3}
        />
        <ErrorState
          title="Failed to load P&L analytics"
          message={pnlError instanceof Error ? pnlError.message : 'API connection error'}
          onRetry={() => refetchPnl()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="animate-stagger-1">
        <PageHeader
          title="P&L Analytics & Returns Attribution"
          description="Comprehensive profit curve diagnostics, daily session returns, win/loss proportions, and risk-adjusted efficiency."
          icon={BarChart3}
          badge={
            <div className="flex items-center gap-2">
              <StatusBadge status="PAPER_MODE" label="PAPER MODE" size="sm" />
              <span className="text-xs font-mono px-2.5 py-0.5 rounded-full bg-slate-900 text-slate-300 border border-slate-800">
                TOTAL P&L: {formatINR(totalRealizedPnl, { showSign: true })}
              </span>
            </div>
          }
        />
      </div>

      {/* Row 1: Key Performance Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-stagger-2">
        {/* Total Realized P&L */}
        <MetricCard
          title="Total Realized P&L"
          value={
            <PnlValue
              value={totalRealizedPnl}
              size="xl"
              showSign={true}
            />
          }
          subtitle={`Across ${totalCompletedCount} completed trades`}
          variant={totalRealizedPnl > 0 ? 'emerald' : totalRealizedPnl < 0 ? 'crimson' : 'default'}
          icon={TrendingUp}
        />

        {/* Weekly Realized */}
        <MetricCard
          title="Weekly Realized P&L"
          value={
            <PnlValue
              value={weekData?.weekly_pnl ?? pnl?.weekly_pnl}
              size="xl"
              showSign={true}
            />
          }
          subtitle="Current calendar week (Mon - Fri)"
          variant={(weekData?.weekly_pnl ?? pnl?.weekly_pnl ?? 0) > 0 ? 'emerald' : 'default'}
          icon={TrendingUp}
        />

        {/* Today Realized */}
        <MetricCard
          title="Today Realized P&L"
          value={
            <PnlValue
              value={todayData?.today_pnl ?? pnl?.today_pnl}
              size="xl"
              showSign={true}
            />
          }
          subtitle={`Trades today: ${todayData?.trades_count ?? 0}`}
          variant={(todayData?.today_pnl ?? pnl?.today_pnl ?? 0) > 0 ? 'emerald' : 'default'}
          icon={Award}
        />

        {/* Win Rate */}
        <MetricCard
          title="Win Rate"
          value={
            calculatedWinRate !== null ? (
              <span className="text-emerald-400">
                {calculatedWinRate.toFixed(1)}%
              </span>
            ) : (
              <span className="text-slate-400">—</span>
            )
          }
          subtitle={
            totalCompletedCount > 0
              ? `${winsCount} Wins / ${lossesCount} Losses`
              : 'No completed trades'
          }
          variant={calculatedWinRate !== null ? 'cyan' : 'default'}
          icon={Scale}
        />
      </div>

      {/* Row 2: Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-stagger-3">
        {/* Chart 1: Daily Session Realized P&L */}
        <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/40 space-y-4 interactive-card">
          <div className="flex items-center justify-between pb-2 border-b border-slate-800">
            <div>
              <h3 className="text-sm font-mono font-bold text-slate-200 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-emerald-400" />
                <span>Daily Session Realized P&L</span>
              </h3>
              <p className="text-[11px] text-slate-400 font-sans mt-0.5">
                Session by session return distribution in INR
              </p>
            </div>
            <span className="text-[10px] font-mono text-slate-400 bg-slate-800 px-2 py-0.5 rounded">
              INR (₹)
            </span>
          </div>

          {rawHistory.length === 0 ? (
            <div className="h-64 flex items-center justify-center">
              <EmptyState title="No daily session data" description="Daily returns will populate here as sessions close." />
            </div>
          ) : (
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rawHistory} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                  <XAxis
                    dataKey="date"
                    stroke="#64748b"
                    fontSize={11}
                    tickLine={false}
                    fontFamily="JetBrains Mono, monospace"
                  />
                  <YAxis
                    stroke="#64748b"
                    fontSize={11}
                    tickLine={false}
                    fontFamily="JetBrains Mono, monospace"
                    tickFormatter={(val) => `₹${val}`}
                  />
                  <Tooltip content={<CustomPnlTooltip />} />
                  <ReferenceLine y={0} stroke="#334155" strokeWidth={1} />
                  <Bar dataKey="pnl" radius={[4, 4, 0, 0]}>
                    {rawHistory.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.pnl >= 0 ? '#10b981' : '#f43f5e'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Chart 2: Cumulative Profit Growth Curve */}
        <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/40 space-y-4 interactive-card">
          <div className="flex items-center justify-between pb-2 border-b border-slate-800">
            <div>
              <h3 className="text-sm font-mono font-bold text-slate-200 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-cyan-400" />
                <span>Cumulative Profit Curve</span>
              </h3>
              <p className="text-[11px] text-slate-400 font-sans mt-0.5">
                Total cumulative equity trajectory over rolling period
              </p>
            </div>
            <span className="text-[10px] font-mono text-cyan-400 bg-cyan-950/60 border border-cyan-800/50 px-2 py-0.5 rounded">
              Realized Growth
            </span>
          </div>

          {cumulativeData.length === 0 ? (
            <div className="h-64 flex items-center justify-center">
              <EmptyState title="No curve data" description="Cumulative growth points will appear as sessions close." />
            </div>
          ) : (
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={cumulativeData} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
                  <defs>
                    <linearGradient id="pnlGrowthGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                  <XAxis
                    dataKey="date"
                    stroke="#64748b"
                    fontSize={11}
                    tickLine={false}
                    fontFamily="JetBrains Mono, monospace"
                  />
                  <YAxis
                    stroke="#64748b"
                    fontSize={11}
                    tickLine={false}
                    fontFamily="JetBrains Mono, monospace"
                    tickFormatter={(val) => `₹${val}`}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const val = payload[0].value as number;
                        return (
                          <div className="p-2.5 rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface-elevated)] shadow-xl text-xs font-mono">
                            <div className="text-[var(--text-muted)] mb-1">{label}</div>
                            <div className="font-bold text-cyan-400">
                              Cumulative: {formatINR(val, { showSign: true })}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="cumulativePnl"
                    stroke="#06b6d4"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#pnlGrowthGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Row 3: Trade Statistics (Best / Worst / Averages) */}
      <div className="space-y-3 animate-stagger-4">
        <h3 className="text-xs font-mono font-bold tracking-wider text-slate-400 uppercase">
          Trade Statistics & Expectancy
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Avg Win */}
          <div className="p-4 rounded-lg border border-slate-800 bg-slate-900/40 space-y-2 interactive-card">
            <span className="text-[10px] font-mono uppercase text-slate-500 flex items-center gap-1.5">
              <PieChart className="w-3.5 h-3.5 text-emerald-400" />
              AVERAGE WINNING TRADE
            </span>
            <div className="text-xl font-bold font-mono text-emerald-400 tabular-nums">
              {avgWinVal !== null ? formatINR(avgWinVal, { showSign: true }) : '—'}
            </div>
            <span className="text-xs text-slate-400 font-mono block">
              {winsCount > 0 ? `Across ${winsCount} winning trades` : 'No winning trades yet'}
            </span>
          </div>

          {/* Avg Loss */}
          <div className="p-4 rounded-lg border border-slate-800 bg-slate-900/40 space-y-2 interactive-card">
            <span className="text-[10px] font-mono uppercase text-slate-500 flex items-center gap-1.5">
              <PieChart className="w-3.5 h-3.5 text-rose-400" />
              AVERAGE LOSING TRADE
            </span>
            <div className="text-xl font-bold font-mono text-rose-400 tabular-nums">
              {avgLossVal !== null ? formatINR(avgLossVal, { showSign: true }) : '—'}
            </div>
            <span className="text-xs text-slate-400 font-mono block">
              {lossesCount > 0 ? `Across ${lossesCount} losing trades` : 'No losing trades yet'}
            </span>
          </div>

          {/* Best Trade */}
          <div className="p-4 rounded-lg border border-slate-800 bg-slate-900/40 space-y-2 interactive-card">
            <span className="text-[10px] font-mono uppercase text-slate-500 flex items-center gap-1.5">
              <Award className="w-3.5 h-3.5 text-cyan-400" />
              BEST TRADE
            </span>
            <div className="text-xl font-bold font-mono text-emerald-400 tabular-nums">
              {bestTradeVal !== null ? formatINR(bestTradeVal, { showSign: true }) : '—'}
            </div>
            <span className="text-xs text-slate-400 font-mono block">
              Highest single trade gain
            </span>
          </div>

          {/* Worst Trade */}
          <div className="p-4 rounded-lg border border-slate-800 bg-slate-900/40 space-y-2 interactive-card">
            <span className="text-[10px] font-mono uppercase text-slate-500 flex items-center gap-1.5">
              <Scale className="w-3.5 h-3.5 text-amber-400" />
              WORST TRADE
            </span>
            <div className="text-xl font-bold font-mono text-rose-400 tabular-nums">
              {worstTradeVal !== null ? formatINR(worstTradeVal, { showSign: true }) : '—'}
            </div>
            <span className="text-xs text-slate-400 font-mono block">
              Maximum single trade drawdown
            </span>
          </div>
        </div>
      </div>

      {/* Row 4: Risk-Adjusted Ratios (Profit Factor & Drawdown) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-stagger-4">
        <div className="p-4 rounded-lg border border-slate-800 bg-slate-900/40 space-y-2 interactive-card">
          <span className="text-[10px] font-mono uppercase text-slate-500 flex items-center gap-1.5">
            <Scale className="w-3.5 h-3.5 text-cyan-400" />
            PROFIT FACTOR
          </span>
          <div className="text-2xl font-bold font-mono text-cyan-300 tabular-nums">
            {calculatedProfitFactor}
          </div>
          <span className="text-xs text-slate-400 font-mono block">
            Gross profit divided by gross loss
          </span>
        </div>

        <div className="p-4 rounded-lg border border-slate-800 bg-slate-900/40 space-y-2 interactive-card">
          <span className="text-[10px] font-mono uppercase text-slate-500 flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5 text-rose-400" />
            MAX PEAK-TO-TROUGH DRAWDOWN
          </span>
          <div className="text-2xl font-bold font-mono text-rose-400 tabular-nums">
            {maxDrawdown !== null ? formatINR(-maxDrawdown, { showSign: true }) : '—'}
          </div>
          <span className="text-xs text-slate-400 font-mono block">
            {maxDrawdown !== null ? 'Peak-to-trough from realized P&L curve' : 'Insufficient historical data'}
          </span>
        </div>
      </div>
    </div>
  );
};
